// StatusChip - the single status pill for the whole Hire app (ported from AIMS).
// One component covers every status domain; switch on `domain` -> tone.
// Renders an inline pill using CSS-variable tokens so it reacts to light/dark
// automatically. Always renders a text label (never colour alone) for a11y.
//
//   <StatusChip domain="booking" value="Return Inspection" dot />
//
// The `booking` domain preserves Hire's established status hues (also used by the
// wallboard) via the --status-* tokens, rendered in the soft-panel chip form.

import React from 'react';

// tone -> {color token, background token}
const TONES = {
  pass: { color: 'var(--pass)', bg: 'var(--pass-bg)' },
  fail: { color: 'var(--fail)', bg: 'var(--fail-bg)' },
  warn: { color: 'var(--warn)', bg: 'var(--warn-bg)' },
  info: { color: 'var(--info)', bg: 'var(--info-bg)' },
  neutral: { color: 'var(--neutral)', bg: 'var(--neutral-bg)' },
  primary: { color: 'var(--primary)', bg: 'var(--primary-tint)' },
  // Booking-status hues (Hire) - preserve the recognisable colour identity.
  prebooking: { color: 'var(--status-prebooking)', bg: 'var(--status-prebooking-bg)' },
  booked: { color: 'var(--status-booked)', bg: 'var(--status-booked-bg)' },
  out: { color: 'var(--status-out)', bg: 'var(--status-out-bg)' },
  returned: { color: 'var(--status-returned)', bg: 'var(--status-returned-bg)' },
  returninspection: { color: 'var(--status-returninspection)', bg: 'var(--status-returninspection-bg)' },
  invoiced: { color: 'var(--status-invoiced)', bg: 'var(--status-invoiced-bg)' },
};

const norm = (v) => String(v ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');

// domain -> (normalisedValue -> tone)
const DOMAIN_TONE = {
  booking: {
    PREBOOKING: 'prebooking',
    BOOKED: 'booked',
    OUT: 'out',
    RETURNED: 'returned',
    RETURN_INSPECTION: 'returninspection',
    INVOICED: 'invoiced',
  },
};

// Booking statuses are shown Title Case with their real words (e.g. "Return
// Inspection"); everything else falls back to the same humaniser.
function humanLabel(value) {
  const n = norm(value);
  const words = n.toLowerCase().split('_');
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function StatusChip({
  value,
  domain = 'booking',
  label,
  dot = false,
  size = 'sm', // 'sm' | 'md'
  style,
  ...rest
}) {
  if (value === null || value === undefined || value === '') return null;

  const n = norm(value);
  const tone = (DOMAIN_TONE[domain] && DOMAIN_TONE[domain][n]) || 'neutral';
  const { color, bg } = TONES[tone] || TONES.neutral;
  const text = label != null ? label : humanLabel(value);

  const pad = size === 'md' ? '5px 11px' : '3px 9px';
  const fontSize = size === 'md' ? 11 : 9.5;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        color,
        background: bg,
        padding: pad,
        borderRadius: 'var(--r-pill)',
        fontSize,
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '.2px',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {dot && <span style={{ fontSize: '0.85em', lineHeight: 1 }}>●</span>}
      {text}
    </span>
  );
}
